import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { ItemCard } from '@/components/ItemCard'
import { Alert, Button, EmptyState, Input, PageSpinner, Select } from '@/components/ui'
import { byUrgency, derive } from '@/domain/items'
import { KIND_LABELS, KINDS, STATUSES } from '@/domain/types'
import type { Kind, Status } from '@/domain/types'
import { errorMessage } from '@/lib/pb'
import { useItems } from '@/lib/queries'

export function Items() {
  const { data: items, isPending, error } = useItems()
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<Kind | 'all'>('all')
  // Defaults to active: archived records are kept for cost history, and showing
  // them by default would bury the things that still need attention.
  const [status, setStatus] = useState<Status | 'all'>('active')

  const visible = useMemo(() => {
    if (!items) return []
    const needle = query.trim().toLowerCase()
    return items
      .filter((item) => kind === 'all' || item.kind === kind)
      .filter((item) => status === 'all' || item.status === status)
      .filter(
        (item) =>
          needle === '' ||
          item.name.toLowerCase().includes(needle) ||
          item.provider.toLowerCase().includes(needle) ||
          item.note.toLowerCase().includes(needle),
      )
      .map((item) => derive(item))
      .sort(byUrgency)
  }, [items, query, kind, status])

  if (isPending) return <PageSpinner />
  if (error) return <Alert>{errorMessage(error)}</Alert>

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder="Search name, provider or note"
            aria-label="Search records"
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <Select
          aria-label="Filter by kind"
          className="sm:w-40"
          value={kind}
          onChange={(e) => setKind(e.target.value as Kind | 'all')}
        >
          <option value="all">All kinds</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k]}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Filter by status"
          className="sm:w-36"
          value={status}
          onChange={(e) => setStatus(e.target.value as Status | 'all')}
        >
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s[0]?.toUpperCase()}
              {s.slice(1)}
            </option>
          ))}
        </Select>
      </div>

      <p className="text-xs text-ink-muted" aria-live="polite">
        {visible.length} {visible.length === 1 ? 'record' : 'records'}
      </p>

      {visible.length === 0 ? (
        <EmptyState
          title="No records match"
          body="Try a different search, or widen the filters."
          action={
            <Link to="/items/new">
              <Button variant="secondary">Add a record</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-2">
          {visible.map((derived) => (
            <ItemCard key={derived.item.id} derived={derived} />
          ))}
        </div>
      )}
    </div>
  )
}
